/**
 * Plan de envío por día. Meta limita a cuántas personas DISTINTAS se les puede escribir cada 24 h
 * (250 al empezar): una campaña de 612 no sale de una vez, sale en tandas. Mostrarlo antes de
 * confirmar evita el «¿por qué se frenó?» del día siguiente. Con la hora de cada tanda (`hasta`, el
 * plan que simula el motor) cada barra dice a qué hora sale: las 24 h son móviles, y la tanda de
 * mañana sale a la hora en que salió la de hoy, no cuando abre la franja.
 *
 * Una sola serie y un solo color (violeta): la altura ya dice la cantidad, y cada barra lleva su
 * número escrito (el color nunca es el único canal).
 */

import type { Cupo24h, PlanTandas as Plan } from "../types";
import { partesDeFecha, textoPlan } from "../logic/chequeo";
import { num } from "../../campana-nueva/utils/format";

const DIAS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
/** "19:00" de un instante ISO, en `zona` (sin ella, la del navegador); "" si no es una fecha con hora */
function hora(s: string | undefined, zona?: string): string {
  if (!s || /^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  return partesDeFecha(s, zona)?.hora ?? "";
}

/** "jue 24/09": "2026-09-24" es ese día en cualquier zona; un instante, el día en `zona` (sin ella, la del navegador). */
function etiquetaDia(s: string, zona?: string): string {
  const p = partesDeFecha(s, zona);
  if (!p) return s || "—";
  const [a, m, d] = p.dia.split("-").map(Number);
  return `${DIAS[new Date(Date.UTC(a, m - 1, d)).getUTCDay()]} ${p.dia.slice(8, 10)}/${p.dia.slice(5, 7)}`;
}

interface Props {
  plan: Plan;
  cupo24h?: Cupo24h | null;
  /** Leyenda al pie (en el detalle: «Plan calculado al confirmar…»). */
  leyenda?: string;
  /** Para decir «hoy»; se inyecta en los tests. */
  hoy?: Date;
  /** La zona de la campaña: el día y la hora de cada tanda van en ella (sin ella, en la del navegador). */
  zona?: string;
}

export function PlanTandas({ plan, cupo24h, leyenda, hoy, zona }: Props) {
  const tandas = (plan?.tandas || []).filter((t) => Number(t.cantidad) > 0);
  const max = Math.max(1, ...tandas.map((t) => Number(t.cantidad) || 0));
  const limite = cupo24h && !cupo24h.ilimitado && cupo24h.limite ? cupo24h.limite : null;

  return (
    <div>
      {tandas.length > 0 ? (
        <ol className="flex items-end gap-2 h-32 overflow-x-auto pb-1" aria-label="Mensajes por día">
          {tandas.map((t) => (
            <li key={t.fecha} className="flex min-w-[52px] flex-1 flex-col items-center justify-end h-full">
              <span className="text-[11px] font-semibold text-slate-700 tabular-nums">{num(t.cantidad)}</span>
              <div className="mt-1 w-full max-w-[44px] rounded-t-md bg-violet-100 flex items-end" style={{ height: "80%" }}>
                <div className="w-full rounded-t-md bg-violet-600" style={{ height: `${Math.max(4, Math.round((Number(t.cantidad) / max) * 100))}%` }} />
              </div>
              <span className="mt-1 text-[10px] text-slate-500 whitespace-nowrap">{etiquetaDia(t.fecha, zona)}</span>
              {t.hasta && hora(t.fecha, zona) && <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap">{hora(t.fecha, zona)}</span>}
            </li>
          ))}
        </ol>
      ) : null}
      <p className="mt-3 text-sm text-slate-700">{textoPlan(plan, hoy || new Date(), zona)}</p>
      {Number(plan?.dias) > 1 && (
        <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">
          {limite ? `Meta te deja escribirle a ${num(limite)} personas distintas cada 24 h.` : "Meta limita a cuántas personas distintas se les escribe cada 24 h."}{" "}
          {/* Verificar el negocio sólo lleva a 2.000: con 2.000 o más ya no sube por ahí. */}
          {limite !== null && limite >= 2000
            ? "Se sube usando al menos la mitad del límite 7 días seguidos con buena calidad."
            : "Se sube usando al menos la mitad del límite 7 días seguidos con buena calidad, o verificando el negocio (pasa a 2.000)."}
        </p>
      )}
      {leyenda && <p className="mt-1.5 text-[11px] text-slate-400">{leyenda}</p>}
    </div>
  );
}

export default PlanTandas;
